var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as jsjoda from '@js-joda/core';
import { delay, forever } from './promise';
let requestInterceptors = [];
let responseInterceptors = [];
export const addRequestInterceptor = (f) => {
    requestInterceptors.push(f);
    return () => {
        requestInterceptors = requestInterceptors.filter(x => x !== f);
    };
};
export const addResponseInterceptor = (f) => {
    responseInterceptors.push(f);
    return () => {
        responseInterceptors = responseInterceptors.filter(x => x !== f);
    };
};
export const runRequestInterceptors = (init) => requestInterceptors.reduce((x, f) => f(x), init);
export const runResponseInterceptors = (res, handled) => __awaiter(void 0, void 0, void 0, function* () {
    return responseInterceptors.reduce((p, f) => __awaiter(void 0, void 0, void 0, function* () {
        const x = yield p;
        return f(x, handled.includes(x.status));
    }), Promise.resolve(res));
});
/**
 * The body of a successful response. A 204 (or any success with nothing in it) resolves to undefined
 * rather than failing to parse; everything else is JSON.
 */
const jsonBody = (r) => __awaiter(void 0, void 0, void 0, function* () {
    if (r.status === 204 || r.status === 205)
        return undefined;
    const text = yield r.text();
    return (text === '' ? undefined : JSON.parse(text));
});
const fieldErrors = (problem) => { var _a; return Object.entries((_a = problem.errors) !== null && _a !== void 0 ? _a : {}).flatMap(([field, messages]) => messages.map((message) => ({ field, message }))); };
const json = (init) => (Object.assign(Object.assign({}, init), { headers: Object.assign(Object.assign({}, (init.headers || {})), { 'Content-Type': 'application/json' }) }));
/**
 * A response no mapping turned into a value. The response interceptors have already seen it, and the
 * app toasts it there, so a caller's `catch` or `finally` only puts its own state back.
 */
export class RequestError extends Error {
    constructor(url, status) {
        super(`Request failed with status ${status}`);
        this.url = url;
        this.status = status;
        this.name = 'RequestError';
    }
}
/**
 * A failure rejects: a status no mapping handles with a RequestError, anything else (the network, a
 * body that won't parse) with its own error. A request that was aborted never settles, since whoever
 * aborted it is no longer waiting.
 */
const __request = (url, m, c) => __awaiter(void 0, void 0, void 0, function* () {
    let _status = null;
    try {
        const init = runRequestInterceptors(c || {});
        const response = yield runResponseInterceptors(yield fetch(url, init), [400, 403, 404, 409, 422, 500].filter((x) => x in m && !!m[x]));
        const status = _status = response.status;
        // Any success is success: 200, 201 Created, 204 No Content. Awaited here, so a body that fails to
        // read or parse lands in the catch below.
        if (response.ok)
            return yield m[200](response);
        switch (status) {
            case 400:
            case 403:
            case 404:
            case 409:
            case 422:
            case 500:
                if (m[status])
                    return yield m[status](response);
        }
        throw new RequestError(url, status);
    }
    catch (e) {
        if (e instanceof Error && e.name === 'AbortError') {
            console.info(`_request.aborted - ${url}`);
            return forever();
        }
        console.error(`_request.error - ${url}: ${_status}`, e);
        throw e;
    }
});
const toSearchParams = (params) => new URLSearchParams(Object.entries(params)
    .filter(([, v]) => v !== null && v !== undefined)
    .flatMap(([k, v]) => Array.isArray(v)
    ? v.map(item => [k, String(item)])
    : [[k, String(v)]]));
export const getConst = (x) => (_c, m) => m({ 200: () => __awaiter(void 0, void 0, void 0, function* () { return x; }) })[200](new Response());
export const getPromise = (x) => (_c, m) => m({ 200: () => __awaiter(void 0, void 0, void 0, function* () { return x; }) })[200](new Response());
export const getJson = (url, searchParams) => {
    const qs = searchParams
        ? `?${toSearchParams(searchParams).toString()}`
        : '';
    return (c, m) => __request(url + qs, m({ 200: jsonBody }), c({}));
};
function stringifier(k, v) {
    if (this[k] instanceof jsjoda.LocalDate) {
        return this[k].format(jsjoda.DateTimeFormatter.ofPattern('yyyy-MM-dd'));
    }
    if (this[k] instanceof jsjoda.LocalTime) {
        return this[k].format(jsjoda.DateTimeFormatter.ofPattern('HH:mm:ss'));
    }
    if (this[k] instanceof jsjoda.LocalDateTime) {
        return this[k].format(jsjoda.DateTimeFormatter.ofPattern('yyyy-MM-dd HH:mm:ss'));
    }
    return v;
}
export const postJson = (url, body) => (c, m) => __request(url, m({ 200: jsonBody }), c(json({ 'method': 'POST', 'body': JSON.stringify(body, stringifier) })));
export const putJson = (url, body) => (c, m) => __request(url, m({ 200: jsonBody }), c(json({ 'method': 'PUT', 'body': JSON.stringify(body, stringifier) }))); //abstract, structure could pass in method?
export const deleteJson = (url, body) => (c, m) => __request(url, m({ 200: jsonBody }), c(json({ 'method': 'DELETE', 'body': JSON.stringify(body, stringifier) })));
export const withDone = (f) => () => {
    return f((x) => x, (x) => x);
};
export const withMap = (r, f) => (c, m) => r(c, (m2) => m(Object.assign(Object.assign({}, m2), { 200: (x) => __awaiter(void 0, void 0, void 0, function* () { return f(yield m2[200](x)); }) })));
export const withDelay = (n, f) => (c, m) => f(c, (m2) => m(Object.assign(Object.assign({}, m2), { 200: (x) => __awaiter(void 0, void 0, void 0, function* () { yield delay(n); return m2[200](x); }) })));
export const withAbort = (f) => {
    const controller = new AbortController();
    return [(c, m) => f((x) => c(Object.assign(Object.assign({}, x), { 'signal': controller.signal })), m), () => { controller.abort(); }];
};
export const withValidate = (f) => (c, m) => f(c, (m2) => m(Object.assign(Object.assign({}, m2), { 200: (x) => __awaiter(void 0, void 0, void 0, function* () { return [true, yield m2[200](x)]; }), 422: (x) => __awaiter(void 0, void 0, void 0, function* () { return [false, fieldErrors(yield x.json())]; }) })));
/** A refusal the server explains: a 409 Conflict problem's detail, rather than the result. */
export const withConflict = (f) => (c, m) => f(c, (m2) => m(Object.assign(Object.assign({}, m2), { 200: (x) => __awaiter(void 0, void 0, void 0, function* () { return [true, yield m2[200](x)]; }), 409: (x) => __awaiter(void 0, void 0, void 0, function* () { var _a; return [false, (_a = (yield x.json()).detail) !== null && _a !== void 0 ? _a : '']; }) })));
export const withOptional = (f) => {
    return (c, m) => f(c, (m2) => m(Object.assign(Object.assign({}, m2), { 404: (_) => __awaiter(void 0, void 0, void 0, function* () { return null; }) })));
};
